import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { Box, Heading, Text } from "@chakra-ui/react";
import { Loader2 } from "lucide-react";
import { useHistory } from "react-router-dom";

import Card from "@/components/Card/Card";
import CardBody from "@/components/Card/CardBody";
import CardHeader from "@/components/Card/CardHeader";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import { Button } from "@/components/ui/button";
import { useActiveBrokerAccount } from "@/contexts/ActiveBrokerAccountContext";
import { useDisplayCurrency } from "@/contexts/DisplayCurrencyContext";
import { useTradeStatistics } from "@/hooks/useTradeStatistics";
import type { TradeStatisticsFilters } from "@/types/trade";
import { miniStatGridTemplateColumns } from "@/utils/miniStatGridLayout";
import {
  MINI_STAT_COUNT_GRADIENT,
  resolveMiniStatGradient,
} from "@/utils/miniStatGradient";
import { toastWorkspaceApiError } from "../../../../utils/workspaceApiErrors";
import TradeLogMiniStatistics from "../../TradeLog/components/TradeLogMiniStatistics";
import { WorkspaceAccountNotesSection } from "@/components/Notes/WorkspaceAccountNotesSection";
import { reportDetailSectionCardProps } from "../../Reports/components/ReportsCardChrome";
import { WorkspaceDrawerActionRow } from "../WorkspaceDrawerActionRow";
import {
  EXECUTING_ACCOUNT_SUMMARY_PRESET_OPTIONS,
  formatExecutingAccountWinRatePercent,
  type ExecutingAccountStatsTimeframe,
} from "../executingAccountSummaryPresets";
import { WORKSPACE_HUB_PATH } from "../workspaceRoutes";
import type { WorkspaceExecutingAccountDrawerTarget } from "../workspaceDrawerEntry";

const REPORT_PRIMARY_FONT = "var(--font-primary)";
const EXECUTING_ACCOUNT_DRAWER_TYPE_LABEL_CLASS =
  "text-[color:var(--brand-tropical-surf)] dark:text-sidebar-muted-foreground";
const EXECUTING_ACCOUNT_DRAWER_HEADING_CLASS =
  "text-[color:var(--brand-tropical-surf)] dark:text-white";
const EXECUTING_ACCOUNT_DRAWER_SUBTLE_TEXT_CLASS =
  "text-gray-500 dark:text-sidebar-muted-foreground";

export type WorkspaceExecutingAccountDrawerContentProps = {
  target: WorkspaceExecutingAccountDrawerTarget;
  onClose?: () => void;
};

export function WorkspaceExecutingAccountDrawerContent({
  target,
  onClose,
}: WorkspaceExecutingAccountDrawerContentProps): React.ReactElement {
  const { formatMoney } = useDisplayCurrency();
  const { setActiveTradeAccount } = useActiveBrokerAccount();
  const history = useHistory();
  const [openingWorkspace, setOpeningWorkspace] = useState(false);
  const [preset, setPreset] = useState<ExecutingAccountStatsTimeframe>("30-days");
  const accountCode = target.account.brokerAccountCode.trim();
  const brokerName = target.account.brokerName.trim().toLowerCase() || null;
  const label = target.label.trim() || accountCode || "Account";

  const handleOpenWorkspace = useCallback(async () => {
    setOpeningWorkspace(true);
    try {
      await setActiveTradeAccount(
        target.account.brokerName,
        target.account.brokerAccountCode
      );
      history.push(WORKSPACE_HUB_PATH);
    } catch (err) {
      toastWorkspaceApiError(err, "Failed to open workspace");
    } finally {
      setOpeningWorkspace(false);
    }
  }, [history, setActiveTradeAccount, target.account]);

  const filters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!accountCode) return undefined;
    return {
      broker_account_code: accountCode,
      ...(brokerName ? { broker_name: brokerName } : {}),
      timeframe: preset,
      fields: "basic",
    };
  }, [accountCode, brokerName, preset]);

  const { statistics, loading } = useTradeStatistics(filters, {
    enabled: Boolean(filters),
  });

  const pnl = statistics?.total_pnl;
  const winRate = statistics?.win_rate;
  const tradeCount = statistics?.closed_trades ?? null;

  const pnlAmount =
    typeof pnl === "number" && Number.isFinite(pnl)
      ? formatMoney(pnl, { showSign: true })
      : "—";
  const winRateAmount = formatExecutingAccountWinRatePercent(winRate);
  const tradesAmount =
    typeof tradeCount === "number" && Number.isFinite(tradeCount)
      ? tradeCount.toLocaleString()
      : "—";

  const metrics = useMemo(
    () => [
      {
        id: "pnl",
        title: "PnL",
        amount: loading ? "…" : pnlAmount,
        backgroundGradient: resolveMiniStatGradient({
          amount: loading ? "…" : pnlAmount,
          percentage: null,
        }),
      },
      {
        id: "winRate",
        title: "Win rate",
        amount: loading ? "…" : winRateAmount,
        backgroundGradient: MINI_STAT_COUNT_GRADIENT,
      },
      {
        id: "trades",
        title: "Trades",
        amount: loading ? "…" : tradesAmount,
        backgroundGradient: MINI_STAT_COUNT_GRADIENT,
      },
    ],
    [loading, pnlAmount, tradesAmount, winRateAmount]
  );

  const gridTemplateColumns = useMemo(
    () =>
      miniStatGridTemplateColumns(metrics, {
        maxColumns: 3,
        gapPx: 24,
      }),
    [metrics]
  );

  return (
    <div
      data-workspace-executing-account-drawer=""
      className="flex min-h-full min-w-0 flex-1 flex-col gap-4"
    >
      <WorkspaceDrawerActionRow onClose={onClose}>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0"
          disabled={openingWorkspace}
          onClick={() => void handleOpenWorkspace()}
        >
          {openingWorkspace ? (
            <>
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
              Opening…
            </>
          ) : (
            "Open workspace"
          )}
        </Button>
      </WorkspaceDrawerActionRow>

      <Card {...reportDetailSectionCardProps}>
        <CardHeader pb="0" w="100%" minW={0}>
          <Box data-reports-card-chrome="" w="100%" minW={0}>
            <Text
              fontSize="xs"
              fontWeight="medium"
              fontFamily={REPORT_PRIMARY_FONT}
              textTransform="uppercase"
              letterSpacing="wide"
              mb={1}
              className={EXECUTING_ACCOUNT_DRAWER_TYPE_LABEL_CLASS}
            >
              Executing account
            </Text>
            <Heading
              as="h2"
              noOfLines={3}
              title={label}
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="normal"
              fontFamily="'Urbanist', sans-serif"
              lineHeight="1.15"
              mt={0}
              mb={0}
              minW={0}
              maxW="100%"
              className={EXECUTING_ACCOUNT_DRAWER_HEADING_CLASS}
            >
              {label}
            </Heading>
            <Text
              mt={2}
              fontSize="sm"
              fontFamily={REPORT_PRIMARY_FONT}
              className={EXECUTING_ACCOUNT_DRAWER_SUBTLE_TEXT_CLASS}
            >
              {brokerName ? `${brokerName} · ${accountCode}` : accountCode}
            </Text>
          </Box>
        </CardHeader>
      </Card>

      <Card {...reportDetailSectionCardProps}>
        <CardHeader
          pb={{ base: 3, md: 4 }}
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={3}
          flexWrap="wrap"
        >
          <Box flex="1" minW={{ base: "100%", md: "200px" }}>
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="normal"
              fontFamily={REPORT_PRIMARY_FONT}
              lineHeight="1.15"
              className={EXECUTING_ACCOUNT_DRAWER_HEADING_CLASS}
            >
              Performance
            </Text>
            <Text
              fontSize="sm"
              mt={1}
              fontFamily={REPORT_PRIMARY_FONT}
              className={EXECUTING_ACCOUNT_DRAWER_SUBTLE_TEXT_CLASS}
            >
              Live account stats for the selected date range.
            </Text>
          </Box>
          <Box
            flexShrink={0}
            w={{ base: "100%", md: "auto" }}
            minW={0}
            display="flex"
            justifyContent={{ base: "flex-start", md: "flex-end" }}
            data-workspace-executing-account-date-select=""
          >
            <FieldSelectFit
              filterChrome="card"
              value={preset}
              onValueChange={(value) => {
                setPreset(value as ExecutingAccountStatsTimeframe);
              }}
              options={EXECUTING_ACCOUNT_SUMMARY_PRESET_OPTIONS}
            />
          </Box>
        </CardHeader>
        <CardBody display="flex" flexDirection="column" pt={0} w="full">
          <Box
            w="100%"
            minW={0}
            display="grid"
            gridTemplateColumns={gridTemplateColumns}
            gap="24px"
            data-mini-stats-grid=""
            data-report-overview-metrics-grid=""
            data-workspace-executing-account-drawer-stats=""
          >
            {metrics.map((metric) => (
              <Box key={metric.id} w="100%" minW={0}>
                <TradeLogMiniStatistics
                  title={metric.title}
                  amount={metric.amount}
                  percentage={null}
                  showPercentage={false}
                  backgroundGradient={metric.backgroundGradient}
                  fitAmount
                />
              </Box>
            ))}
          </Box>
        </CardBody>
      </Card>

      <WorkspaceAccountNotesSection account={target.account} />
    </div>
  );
}
